<template>
    <v-app>
        <v-app-bar app flat>
            <v-btn icon="mdi-arrow-left" variant="text" @click="router.push('/dashboard')" />
            <v-app-bar-title class="font-weight-medium">Project Studio</v-app-bar-title>
            <v-spacer></v-spacer>
            <v-chip variant="outlined" prepend-icon="mdi-account-circle" class="mr-2" density="comfortable">
                {{ email }}
            </v-chip>
            <v-menu offset-y>
                <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" text>
                        Menu
                        <v-icon end>mdi-menu-down</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item title="Profile" value="Profile" @click="onProfile" density="compact" prepend-icon="mdi-account-circle"/>
                    <v-list-item title="Logout" value="Logout" @click="onLogout" density="compact" prepend-icon="mdi-logout"/>
                </v-list>
            </v-menu>
        </v-app-bar>

        <v-main>
            <v-container class="py-10">
                <v-row>
                    <v-col cols="12" md="8">
                        <v-card :loading="saving || loadingProject">
                            <v-card-text>
                                <v-form ref="formRef" v-model="formValid" lazy-validation>
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                    v-model="metaForm.projectId"
                                                    label="Project ID"
                                                    hint="Firestore document id"
                                                    persistent-hint
                                                    :rules="[requiredRule]"
                                                    density="comfortable"
                                                    ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                    v-model="metaForm.name"
                                                    label="Name"
                                                    :rules="[requiredRule]"
                                                    density="comfortable"
                                                    ></v-text-field>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                    v-model="metaForm.subtitle"
                                                    label="Subtitle"
                                                    rows="2"
                                                    auto-grow
                                                    density="comfortable"
                                                    ></v-textarea>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-select
                                                    v-model="metaForm.assetLayout"
                                                    :items="[{ title: 'Legacy', value: 'legacy' }, { title: 'Regional', value: 'regional' }]"
                                                    label="Asset layout"
                                                    density="comfortable"
                                                    @update:model-value="layoutTouched = true"
                                                    ></v-select>
                                        </v-col>
                                        <v-col v-if="metaForm.assetLayout === 'regional'" cols="12" md="6">
                                            <v-select
                                                    v-model="metaForm.initialRegion"
                                                    :items="regionOptions"
                                                    item-title="title"
                                                    item-value="value"
                                                    label="Initial region"
                                                    clearable
                                                    density="comfortable"
                                                    hint="Defaults to the first activatable region"
                                                    persistent-hint
                                                    ></v-select>
                                        </v-col>
                                        <v-col v-if="metaForm.assetLayout === 'regional'" cols="12">
                                            <v-textarea
                                                    v-model="regionsJsonText"
                                                    label="Region descriptors (JSON)"
                                                    rows="10"
                                                    auto-grow
                                                    :error-messages="regionsJsonError ? [regionsJsonError] : []"
                                                    hint="Leave empty to derive model, meta, and camera paths from Model names."
                                                    persistent-hint
                                                    density="comfortable"
                                                    ></v-textarea>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                    v-model="modelNamesText"
                                                    label="Model names (one per line)"
                                                    rows="3"
                                                    auto-grow
                                                    hint="Paste or list GLB model names, one per line"
                                                    persistent-hint
                                                    density="comfortable"
                                                    ></v-textarea>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                    v-model="pointCloudNamesText"
                                                    label="Point cloud names (one per line)"
                                                    rows="3"
                                                    auto-grow
                                                    hint="Paste or list PLY point cloud names, one per line"
                                                    persistent-hint
                                                    density="comfortable"
                                                    ></v-textarea>
                                        </v-col>
                                        <v-col v-if="isAdmin" cols="12">
                                            <v-textarea
                                                    v-model="whiteListText"
                                                    label="Whitelisted emails"
                                                    rows="3"
                                                    auto-grow
                                                    hint="One email per line (or comma-separated). These are the only accounts that can view or edit this project. Your own email is always included automatically."
                                                    persistent-hint
                                                    density="comfortable"
                                                    ></v-textarea>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-textarea
                                                    v-model="metaForm.description"
                                                    label="Description"
                                                    rows="3"
                                                    auto-grow
                                                    density="comfortable"
                                                    ></v-textarea>
                                        </v-col>
                                    </v-row>
                                    <v-sheet class="mt-4 pa-4" rounded="lg" variant="outlined">
                                        <div class="d-flex align-center justify-space-between">
                                            <div class="text-subtitle-1 font-weight-medium">Transformation (4x4)</div>
                                            <v-btn size="small" variant="text" prepend-icon="mdi-backup-restore" @click="resetMatrix">
                                                Reset to identity
                                            </v-btn>
                                        </div>
                                        <div class="matrix-grid mt-3">
                                            <v-text-field
                                                    v-for="index in 16"
                                                    :key="index"
                                                    v-model.number="metaForm.transformation[Math.floor((index - 1) / 4)][(index - 1) % 4]"
                                                    type="number"
                                                    hide-details
                                                    density="compact"
                                                    variant="outlined"
                                                    class="matrix-input"
                                                    ></v-text-field>
                                        </div>
                                    </v-sheet>
                                    <v-sheet class="mt-4 pa-4" rounded="lg" variant="outlined">
                                        <div class="d-flex align-center justify-space-between">
                                            <div>
                                                <div class="text-subtitle-1 font-weight-medium">Custom trajectories</div>
                                                <div class="text-caption text-medium-emphasis">
                                                    Coordinates are project world coordinates, after model transformations.
                                                </div>
                                            </div>
                                            <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" @click="addTrajectory">
                                                Add trajectory
                                            </v-btn>
                                        </div>
                                        <v-alert
                                                v-if="trajectoryDrafts.length === 0"
                                                class="mt-3"
                                                type="info"
                                                density="compact"
                                                variant="tonal"
                                                >No custom trajectory. The viewer will derive one from the active region's cameras.</v-alert>
                                        <v-card
                                                v-for="(trajectory, index) in trajectoryDrafts"
                                                :key="trajectory.key"
                                                class="mt-3"
                                                variant="outlined"
                                                >
                                            <v-card-text>
                                                <v-row>
                                                    <v-col cols="12" md="3">
                                                        <v-text-field
                                                                v-model="trajectory.id"
                                                                label="Trajectory ID"
                                                                density="comfortable"
                                                                hide-details="auto"
                                                                @update:model-value="trajectory.error = ''"
                                                                ></v-text-field>
                                                    </v-col>
                                                    <v-col cols="12" md="4">
                                                        <v-text-field
                                                                v-model="trajectory.label"
                                                                label="Display name"
                                                                density="comfortable"
                                                                hide-details="auto"
                                                                ></v-text-field>
                                                    </v-col>
                                                    <v-col cols="12" md="4">
                                                        <v-text-field
                                                                v-model.number="trajectory.startStation"
                                                                label="Starting station"
                                                                type="number"
                                                                step="0.5"
                                                                density="comfortable"
                                                                hide-details="auto"
                                                                hint="Station value at the first point"
                                                                persistent-hint
                                                                @update:model-value="trajectory.error = ''"
                                                                ></v-text-field>
                                                    </v-col>
                                                    <v-col cols="12" md="1" class="d-flex justify-end align-start">
                                                        <v-btn
                                                                icon="mdi-delete-outline"
                                                                color="red"
                                                                variant="text"
                                                                density="comfortable"
                                                                @click="removeTrajectory(index)"
                                                                v-tooltip:bottom="'Remove trajectory'"
                                                                />
                                                    </v-col>
                                                    <v-col cols="12">
                                                        <v-switch
                                                                v-model="trajectory.stationIncreasing"
                                                                label="Station increases along the trajectory"
                                                                color="primary"
                                                                density="compact"
                                                                hide-details
                                                                ></v-switch>
                                                    </v-col>
                                                    <v-col cols="12">
                                                        <v-textarea
                                                                v-model="trajectory.pointsText"
                                                                label="Points (x,y,z — one point per line)"
                                                                rows="60"
                                                                no-resize
                                                                density="comfortable"
                                                                :error-messages="trajectory.error ? [trajectory.error] : []"
                                                                hint="Example: 443240.0,87360.0,479.0"
                                                                persistent-hint
                                                                @update:model-value="trajectory.error = ''"
                                                                ></v-textarea>
                                                    </v-col>
                                                </v-row>
                                            </v-card-text>
                                        </v-card>
                                        <v-select
                                                v-if="trajectoryDrafts.length > 0"
                                                v-model="metaForm.initialTrajectory"
                                                :items="trajectoryOptions"
                                                item-title="title"
                                                item-value="value"
                                                label="Initial trajectory"
                                                class="mt-3"
                                                density="comfortable"
                                                hint="Defaults to the first valid trajectory"
                                                persistent-hint
                                                ></v-select>
                                    </v-sheet>
                                </v-form>
                            </v-card-text>
                            <v-card-actions class="d-flex justify-end">
                                <v-btn color="primary" prepend-icon="mdi-content-save" @click="saveProject" :loading="saving">
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-card variant="elevated" :loading="projectsLoading">
                            <v-card-title class="text-subtitle-1">Existing projects</v-card-title>
                            <v-card-text>
                                <v-autocomplete
                                        v-model="selectedProjectId"
                                        :items="projectOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Load to edit"
                                        prepend-inner-icon="mdi-folder-outline"
                                        clearable
                                        density="comfortable"
                                        @update:model-value="onProjectSelect"
                                        ></v-autocomplete>
                                <v-btn v-if="isAdmin" block class="mt-3" color="primary" variant="tonal" prepend-icon="mdi-plus" @click="startFresh">
                                    Start blank
                                </v-btn>
                                <v-btn
                                        block
                                        class="mt-3"
                                        color="red"
                                        variant="tonal"
                                        prepend-icon="mdi-delete"
                                        :disabled="!selectedProjectId"
                                        @click="confirmDelete = true"
                                        >
                                        Delete project
                                </v-btn>
                                <v-divider class="my-4"></v-divider>
                                <div class="text-subtitle-2 mb-1">Current snapshot</div>
                                <v-chip-group column>
                                    <v-chip variant="flat" color="primary" class="text-body-2">ID: {{ metaForm.projectId || 'not set' }}</v-chip>
                                    <v-chip variant="outlined" color="primary" class="text-body-2">Lines: {{ linesCache.length }}</v-chip>
                                    <v-chip variant="outlined" color="primary" class="text-body-2">Trajectories: {{ trajectoryDrafts.length }}</v-chip>
                                </v-chip-group>
                                <v-alert
                                        v-if="statusColor === 'error'"
                                        class="mt-4"
                                        type="error"
                                        density="compact"
                                        variant="tonal"
                                        >
                                        {{ statusMessage }}
                                </v-alert>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
            <v-snackbar v-model="snackbar" :timeout="3000" color="primary">
                {{ statusMessage }}
            </v-snackbar>
            <v-dialog v-model="confirmDelete" max-width="420">
                <v-card>
                    <v-card-title class="text-h6">Delete project?</v-card-title>
                    <v-card-text>
                        This will remove the project document from Firestore. Lines and meta for <strong>{{ selectedProjectId }}</strong> will be deleted. This action cannot be undone.
                    </v-card-text>
                    <v-card-actions class="d-flex justify-end">
                        <v-btn variant="text" @click="confirmDelete = false">Cancel</v-btn>
                        <v-btn color="red" variant="flat" prepend-icon="mdi-delete" @click="deleteProject" :loading="deleting">
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-main>
    </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useFirestore, useFirebaseAuth, useCollection } from 'vuefire';
import { collection, deleteDoc, doc, getDoc, query, setDoc, where } from '@firebase/firestore';
import type { VForm } from 'vuetify/components';
import { isAdminEmail, onProfile, onLogout } from '/components/ttUtils';
import { useRoute } from 'vue-router';
import router from '../router';
import type {
    AssetLayout, ProjectRegion, ProjectTrajectory, ProjectTrajectoryPoint,
} from '../components/ttProjectConfig';

type Matrix4x4 = [number[], number[], number[], number[]];

interface ProjectMetaForm {
    projectId: string;
    name: string;
    subtitle: string;
    description: string;
    transformation: Matrix4x4;
    modelNames: string[];
    pointCloudNames: string[];
    assetLayout: AssetLayout;
    initialRegion: string;
    initialTrajectory: string;
    projectOwner: string;
    whiteList: string[];
}

interface TrajectoryDraft {
    key: number;
    id: string;
    label: string;
    startStation: number | string;
    stationIncreasing: boolean;
    pointsText: string;
    error: string;
}

const auth = useFirebaseAuth();
const email = auth?.currentUser?.email || '';
const db = useFirestore();

const projects = useCollection(
    query(collection(db, 'projects'), where('meta.whiteList', 'array-contains', email)),
);
const projectsLoading = computed(() => !projects.value);

const route = useRoute();
const isAdmin = ref(false);
onMounted(async () => {
    try {
        isAdmin.value = await isAdminEmail(db, email);
    } catch (error) {
        console.error('Failed to read config/admins:', error);
    }
});


const formRef = ref<VForm | null>(null);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const loadingProject = ref(false);
const snackbar = ref(false);
const confirmDelete = ref(false);
const statusMessage = ref('');
const statusColor = ref<'success' | 'error' | ''>('');

const selectedProjectId = ref<string>('');
const linesCache = ref<any[]>([]);

const metaForm = reactive<ProjectMetaForm>({
    projectId: '',
    name: '',
    subtitle: '',
    description: '',
    transformation: identityMatrix(),
    modelNames: [],
    pointCloudNames: [],
    assetLayout: 'legacy',
    initialRegion: '',
    initialTrajectory: '',
    projectOwner: email,
    whiteList: [email].filter(Boolean),
});

const modelNamesText = ref('');
const pointCloudNamesText = ref('');
const whiteListText = ref(email);
const regionsJsonText = ref('');
const regionsJsonError = ref('');
const loadedMeta = ref<Record<string, unknown>>({});
const loadedAssetLayoutExplicit = ref(false);
const layoutTouched = ref(false);
const trajectoryDrafts = ref<TrajectoryDraft[]>([]);
let nextTrajectoryKey = 1;

watch(modelNamesText, (val) => {
    metaForm.modelNames = parseModelNames(val);
});

watch(pointCloudNamesText, (val) => {
    metaForm.pointCloudNames = parseModelNames(val);
});

watch(whiteListText, (val) => {
    metaForm.whiteList = parseWhiteList(val);
});

watch(regionsJsonText, () => {
    regionsJsonError.value = '';
});

const parsedRegions = computed<ProjectRegion[]>(() => {
    if (!regionsJsonText.value.trim()) return [];
    try {
        const value = JSON.parse(regionsJsonText.value);
        if (!Array.isArray(value)) return [];
        return value.filter((region) => region && typeof region.id === 'string' && typeof region.modelPath === 'string');
    } catch {
        return [];
    }
});

const regionOptions = computed(() => {
    const explicit = parsedRegions.value.map((region) => ({ title: region.label || region.id, value: region.id }));
    if (explicit.length) return explicit;
    return metaForm.modelNames.map((name) => {
        const file = name.replace(/\\/g, '/').split('/').pop() || name;
        const id = file.replace(/\.glb$/i, '');
        return { title: id, value: id };
    });
});

const trajectoryOptions = computed(() => trajectoryDrafts.value.map((trajectory, index) => ({
    title: trajectory.label.trim() || trajectory.id.trim() || `Trajectory ${index + 1}`,
    value: trajectory.id.trim(),
})));

const projectOptions = computed(() => (projects.value || []).map((project) => ({
    title: project.meta?.name ? `${project.meta.name} (${project.id})` : project.id,
    subtitle: project.meta?.subtitle,
    value: project.id,
})));

const requiredRule = (value: string) => !!(value && value.toString().trim().length) || 'Required';

function identityMatrix(): Matrix4x4 {
    return [
        [1, 0, 0, 0],
        [0, 1, 0, 0],
        [0, 0, 1, 0],
        [0, 0, 0, 1],
    ];
}

function toNumber(value: unknown, fallback = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
}

function parseModelNames(value: string): string[] {
    return value
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

function parseWhiteList(value: string): string[] {
    const emails = value
        .split(/[\n,]/)
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.length > 0);
    return Array.from(new Set(emails));
}

function parseTrajectoryPoints(value: string): ProjectTrajectoryPoint[] {
    const lines = value.split(/\r?\n/);
    const points: ProjectTrajectoryPoint[] = [];
    for (let index = 0; index < lines.length; index++) {
        const line = lines[index].trim();
        if (!line) continue;
        const cells = line.split(',').map((cell) => cell.trim());
        if (cells.length !== 3) {
            throw new Error(`Line ${index + 1} must contain exactly three comma-separated values.`);
        }
        if (cells.some((cell) => !cell)) {
            throw new Error(`Line ${index + 1} contains an empty coordinate.`);
        }
        const [x, y, z] = cells.map(Number);
        if (![x, y, z].every(Number.isFinite)) {
            throw new Error(`Line ${index + 1} contains an invalid number.`);
        }
        points.push({ x, y, z });
    }
    if (points.length < 2) throw new Error('A trajectory requires at least two points.');
    const first = points[0];
    if (!points.slice(1).some((point) => point.x !== first.x || point.y !== first.y || point.z !== first.z)) {
        throw new Error('A trajectory requires at least two distinct points.');
    }
    return points;
}

function pointFromUnknown(value: unknown): ProjectTrajectoryPoint | null {
    const toCoordinate = (coordinate: unknown): number => {
        if (typeof coordinate === 'number') return coordinate;
        if (typeof coordinate === 'string' && coordinate.trim()) return Number(coordinate);
        return NaN;
    };
    if (Array.isArray(value)) {
        const [x, y, z] = value.map(toCoordinate);
        return [x, y, z].every(Number.isFinite) ? { x, y, z } : null;
    }
    if (!value || typeof value !== 'object') return null;
    const raw = value as Partial<ProjectTrajectoryPoint>;
    const point = { x: toCoordinate(raw.x), y: toCoordinate(raw.y), z: toCoordinate(raw.z) };
    return [point.x, point.y, point.z].every(Number.isFinite) ? point : null;
}

function formatTrajectoryPoints(value: unknown): string {
    if (!Array.isArray(value)) return '';
    return value
        .map(pointFromUnknown)
        .filter((point): point is ProjectTrajectoryPoint => Boolean(point))
        .map((point) => `${point.x},${point.y},${point.z}`)
        .join('\n');
}

function addTrajectory() {
    const index = trajectoryDrafts.value.length + 1;
    const key = nextTrajectoryKey++;
    const id = `trajectory-${key}`;
    trajectoryDrafts.value.push({
        key,
        id,
        label: `Trajectory ${index}`,
        startStation: 0,
        stationIncreasing: true,
        pointsText: '',
        error: '',
    });
    if (!metaForm.initialTrajectory) metaForm.initialTrajectory = id;
}

function removeTrajectory(index: number) {
    const [removed] = trajectoryDrafts.value.splice(index, 1);
    if (removed?.id === metaForm.initialTrajectory) {
        metaForm.initialTrajectory = trajectoryDrafts.value[0]?.id ?? '';
    }
}

function loadTrajectoryDrafts(value: unknown) {
    trajectoryDrafts.value = Array.isArray(value)
        ? value.filter((entry) => entry && typeof entry === 'object').map((entry, index) => {
            const raw = entry as Partial<ProjectTrajectory>;
            return {
                key: nextTrajectoryKey++,
                id: typeof raw.id === 'string' ? raw.id : `trajectory-${index + 1}`,
                label: typeof raw.label === 'string' ? raw.label : '',
                startStation: Number.isFinite(Number(raw.startStation)) ? Number(raw.startStation) : 0,
                stationIncreasing: raw.stationIncreasing !== false,
                pointsText: formatTrajectoryPoints(raw.points),
                error: '',
            };
        })
        : [];
}

function expandMatrix(value: unknown): Matrix4x4 {
    if (Array.isArray(value) && value.length === 16) {
        return [
            value.slice(0, 4),
            value.slice(4, 8),
            value.slice(8, 12),
            value.slice(12, 16),
        ].map((row, rowIndex) => row.map((cell, colIndex) => toNumber(cell, identityMatrix()[rowIndex][colIndex]))) as Matrix4x4;
    }
    if (Array.isArray(value) && value.length === 4) {
        return (value as unknown[]).map((row, rowIndex) => {
            if (Array.isArray(row) && row.length === 4) {
                return row.map((cell, colIndex) => toNumber(cell, identityMatrix()[rowIndex][colIndex]));
            }
            return identityMatrix()[rowIndex];
        }) as Matrix4x4;
    }
    return identityMatrix();
}

function flattenMatrix(mat: Matrix4x4): number[] {
    return mat.flat().map((cell) => toNumber(cell));
}

function resetMatrix() {
    metaForm.transformation = identityMatrix();
}

function startFresh() {
    statusMessage.value = '';
    statusColor.value = '';
    selectedProjectId.value = '';
    linesCache.value = [];
    metaForm.projectId = '';
    metaForm.name = '';
    metaForm.subtitle = '';
    metaForm.description = '';
    metaForm.modelNames = [];
    metaForm.pointCloudNames = [];
    metaForm.assetLayout = 'legacy';
    metaForm.initialRegion = '';
    metaForm.initialTrajectory = '';
    metaForm.projectOwner = email;
    metaForm.whiteList = [email].filter(Boolean);
    modelNamesText.value = '';
    pointCloudNamesText.value = '';
    whiteListText.value = email;
    regionsJsonText.value = '';
    regionsJsonError.value = '';
    loadedMeta.value = {};
    trajectoryDrafts.value = [];
    loadedAssetLayoutExplicit.value = false;
    layoutTouched.value = false;
    resetMatrix();
}

async function onProjectSelect(projectId: string | null) {
    if (!projectId) {
        startFresh();
        return;
    }
    await loadProject(projectId);
}

async function loadProject(projectId: string) {
    loadingProject.value = true;
    statusMessage.value = '';
    statusColor.value = '';
    try {
        const projectRef = doc(db, 'projects', projectId);
        const snapshot = await getDoc(projectRef);
        if (snapshot.exists()) {
            const data = snapshot.data();
            loadedMeta.value = data.meta && typeof data.meta === 'object' ? { ...data.meta } : {};
            loadedAssetLayoutExplicit.value = data.meta?.assetLayout === 'legacy' || data.meta?.assetLayout === 'regional';
            layoutTouched.value = false;
            metaForm.projectId = projectId;
            metaForm.name = data.meta?.name || '';
            metaForm.subtitle = data.meta?.subtitle || '';
            metaForm.description = data.meta?.description || '';
            metaForm.transformation = expandMatrix(data.meta?.transformation);
            metaForm.modelNames = Array.isArray(data.meta?.modelNames) ? data.meta.modelNames.map((v: unknown) => String(v)) : [];
            metaForm.pointCloudNames = Array.isArray(data.meta?.pointCloudNames) ? data.meta.pointCloudNames.map((v: unknown) => String(v)) : [];
            metaForm.assetLayout = data.meta?.assetLayout === 'regional' ? 'regional' : 'legacy';
            metaForm.initialRegion = typeof data.meta?.initialRegion === 'string' ? data.meta.initialRegion : '';
            metaForm.initialTrajectory = typeof data.meta?.initialTrajectory === 'string' ? data.meta.initialTrajectory : '';
            metaForm.projectOwner = typeof data.meta?.projectOwner === 'string' ? data.meta.projectOwner : email;
            metaForm.whiteList = Array.isArray(data.meta?.whiteList)
                ? parseWhiteList(data.meta.whiteList.join('\n'))
                : [email].filter(Boolean);
            modelNamesText.value = metaForm.modelNames.join('\n');
            pointCloudNamesText.value = metaForm.pointCloudNames.join('\n');
            whiteListText.value = metaForm.whiteList.join('\n');
            regionsJsonText.value = Array.isArray(data.meta?.regions) ? JSON.stringify(data.meta.regions, null, 2) : '';
            loadTrajectoryDrafts(data.meta?.trajectories);
            if (!trajectoryDrafts.value.some((trajectory) => trajectory.id === metaForm.initialTrajectory)) {
                metaForm.initialTrajectory = trajectoryDrafts.value[0]?.id ?? '';
            }
            linesCache.value = Array.isArray(data.lines) ? data.lines : [];
            selectedProjectId.value = projectId;
            statusMessage.value = 'Loaded project meta.';
            statusColor.value = 'success';
            snackbar.value = true;
        } else {
            statusMessage.value = 'Project not found. Starting blank.';
            statusColor.value = 'error';
            snackbar.value = true;
            startFresh();
            metaForm.projectId = projectId;
        }
    } catch (error: any) {
        statusMessage.value = `Failed to load project: ${error.message || error}`;
        statusColor.value = 'error';
        snackbar.value = true;
    } finally {
        loadingProject.value = false;
    }
}

async function saveProject() {
    if (saving.value) return;
    statusMessage.value = '';
    statusColor.value = '';

    const form = formRef.value;
    if (form) {
        const validation = await form.validate();
        if (!validation.valid) {
            return;
        }
    }

    if (!metaForm.projectId) {
        statusMessage.value = 'Project ID is required.';
        statusColor.value = 'error';
        snackbar.value = true;
        return;
    }

    const owner = metaForm.projectOwner.trim().toLowerCase() || email.toLowerCase();
    // Non-admins cannot change who is assigned: keep the stored list (or just the creator for a new project).
    const storedWhiteList = Array.isArray(loadedMeta.value.whiteList) ? loadedMeta.value.whiteList as string[] : [owner];
    const baseWhiteList = isAdmin.value ? metaForm.whiteList : storedWhiteList;
    const whiteList = Array.from(new Set([...baseWhiteList, owner].filter(Boolean)));
    if (whiteList.length === 0) {
        statusMessage.value = 'At least one whitelisted email (yours) is required.';
        statusColor.value = 'error';
        snackbar.value = true;
        return;
    }

    let regions: ProjectRegion[] = [];
    if (metaForm.assetLayout === 'regional' && regionsJsonText.value.trim()) {
        try {
            const value = JSON.parse(regionsJsonText.value);
            if (!Array.isArray(value)) throw new Error('Region descriptors must be a JSON array.');
            for (const [index, region] of value.entries()) {
                if (!region || typeof region.id !== 'string' || !region.id.trim() || typeof region.modelPath !== 'string' || !region.modelPath.trim()) {
                    throw new Error(`Region ${index + 1} requires non-empty id and modelPath strings.`);
                }
            }
            regions = value as ProjectRegion[];
        } catch (error) {
            regionsJsonError.value = error instanceof Error ? error.message : String(error);
            return;
        }
    }

    const trajectories: ProjectTrajectory[] = [];
    const trajectoryIds = new Set<string>();
    let trajectoriesValid = true;
    for (const [index, draft] of trajectoryDrafts.value.entries()) {
        draft.error = '';
        const id = draft.id.trim();
        if (!id) {
            draft.error = 'Trajectory ID is required.';
            trajectoriesValid = false;
            continue;
        }
        if (trajectoryIds.has(id)) {
            draft.error = `Trajectory ID "${id}" is duplicated.`;
            trajectoriesValid = false;
            continue;
        }
        trajectoryIds.add(id);
        const startStation = Number(draft.startStation);
        if (draft.startStation === '' || !Number.isFinite(startStation)) {
            draft.error = 'Starting station must be a valid number.';
            trajectoriesValid = false;
            continue;
        }
        try {
            trajectories.push({
                id,
                label: draft.label.trim() || `Trajectory ${index + 1}`,
                startStation,
                stationIncreasing: draft.stationIncreasing,
                points: parseTrajectoryPoints(draft.pointsText),
            });
        } catch (error) {
            draft.error = error instanceof Error ? error.message : String(error);
            trajectoriesValid = false;
        }
    }
    if (!trajectoriesValid) return;

    saving.value = true;
    try {
        const projectRef = doc(db, 'projects', metaForm.projectId);
        const existingSnap = await getDoc(projectRef);
        if (!existingSnap.exists() && !isAdmin.value) {
            statusMessage.value = 'Only admins can create new projects.';
            statusColor.value = 'error';
            snackbar.value = true;
            return;
        }
        const linesToPersist = linesCache.value.length > 0
            ? linesCache.value
            : (existingSnap.exists() ? existingSnap.data().lines || [] : []);

        const metaPayload: Record<string, unknown> = {
                ...loadedMeta.value,
                name: metaForm.name.trim(),
                subtitle: metaForm.subtitle.trim(),
                description: metaForm.description.trim(),
                transformation: flattenMatrix(metaForm.transformation),
                modelNames: metaForm.modelNames,
                pointCloudNames: metaForm.pointCloudNames,
                projectOwner: owner,
                whiteList,
        };
        if (trajectories.length) {
            metaPayload.trajectories = trajectories;
            metaPayload.initialTrajectory = trajectories.some((trajectory) => trajectory.id === metaForm.initialTrajectory)
                ? metaForm.initialTrajectory
                : trajectories[0].id;
        } else {
            delete metaPayload.trajectories;
            delete metaPayload.initialTrajectory;
        }
        if (metaForm.assetLayout === 'regional') {
            metaPayload.assetLayout = 'regional';
            if (metaForm.initialRegion) metaPayload.initialRegion = metaForm.initialRegion;
            else delete metaPayload.initialRegion;
            if (regions.length) metaPayload.regions = regions;
            else delete metaPayload.regions;
        } else if (loadedAssetLayoutExplicit.value || layoutTouched.value) {
            metaPayload.assetLayout = 'legacy';
            delete metaPayload.initialRegion;
            delete metaPayload.regions;
        } else {
            delete metaPayload.assetLayout;
        }
        const payload: Record<string, unknown> = { meta: metaPayload };

        if (linesToPersist !== undefined) {
            payload.lines = linesToPersist;
        }

        await setDoc(projectRef, payload, { mergeFields: Object.keys(payload) });
        statusMessage.value = 'Project saved to Firestore.';
        statusColor.value = 'success';
        snackbar.value = true;
        selectedProjectId.value = metaForm.projectId;
    } catch (error: any) {
        statusMessage.value = `Failed to save: ${error.message || error}`;
        statusColor.value = 'error';
        snackbar.value = true;
    } finally {
        saving.value = false;
    }
}

async function deleteProject() {
    if (!selectedProjectId.value) {
        confirmDelete.value = false;
        return;
    }
    deleting.value = true;
    statusMessage.value = '';
    statusColor.value = '';
    try {
        await deleteDoc(doc(db, 'projects', selectedProjectId.value));
        statusMessage.value = 'Project deleted.';
        statusColor.value = 'success';
        snackbar.value = true;
        startFresh();
    } catch (error: any) {
        statusMessage.value = `Failed to delete: ${error.message || error}`;
        statusColor.value = 'error';
        snackbar.value = true;
    } finally {
        confirmDelete.value = false;
        deleting.value = false;
    }
}

// Opened from a project's menu: preload that project.
watch(() => route.query.project, (projectId) => {
    if (typeof projectId === 'string' && projectId) loadProject(projectId);
}, { immediate: true });
</script>

<style scoped>
.matrix-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(70px, 1fr));
    gap: 10px;
}

.matrix-input :deep(input) {
    text-align: center;
    font-variant-numeric: tabular-nums;
}
</style>
